import type { ReactNode } from 'react'

import { Card } from '@/components/ui/Card'
import { CompletionBar } from '@/components/ui/CompletionBar'
import { EmptyState } from '@/components/ui/EmptyState'
import { BasicDetailsForm } from '@/features/student/components/BasicDetailsForm'
import { CertificationSection } from '@/features/student/components/CertificationSection'
import { DocumentsSection } from '@/features/student/components/DocumentsSection'
import { EducationSection } from '@/features/student/components/EducationSection'
import { ExperienceSection } from '@/features/student/components/ExperienceSection'
import { FieldVisibilityToggle } from '@/features/student/components/FieldVisibilityToggle'
import { LanguageSection } from '@/features/student/components/LanguageSection'
import { PreferencesForm } from '@/features/student/components/PreferencesForm'
import { ProjectSection } from '@/features/student/components/ProjectSection'
import { SkillSection } from '@/features/student/components/SkillSection'
import { SocialLinkSection } from '@/features/student/components/SocialLinkSection'
import { useStudentProfileQuery } from '@/features/student/hooks/useProfile'

function AccordionItem({
  id,
  title,
  children,
  defaultOpen = false,
}: {
  id: string
  title: string
  children: ReactNode
  defaultOpen?: boolean
}) {
  return (
    <div className="accordion-item">
      <h2 className="accordion-header">
        <button
          className={`accordion-button ${defaultOpen ? '' : 'collapsed'}`.trim()}
          type="button"
          data-bs-toggle="collapse"
          data-bs-target={`#section-${id}`}
        >
          {title}
        </button>
      </h2>
      <div
        id={`section-${id}`}
        className={`accordion-collapse collapse ${defaultOpen ? 'show' : ''}`.trim()}
        data-bs-parent="#profile-accordion"
      >
        <div className="accordion-body">{children}</div>
      </div>
    </div>
  )
}

export function StudentProfilePage() {
  const { data: profile, isPending, isError } = useStudentProfileQuery()

  return (
    <>
      <div className="d-flex align-items-center justify-content-between my-4 page-header-breadcrumb flex-wrap gap-2">
        <div>
          <p className="fw-semibold fs-20 mb-0">My Profile</p>
        </div>
      </div>

      {isPending && (
        <Card>
          <p className="text-muted mb-0">Loading your profile…</p>
        </Card>
      )}

      {isError && (
        <Card>
          <EmptyState
            heading="Couldn't load your profile"
            message="Please refresh the page to try again."
          />
        </Card>
      )}

      {profile && (
        <>
          <Card className="mb-3">
            <CompletionBar
              percent={profile.profileCompletionPercent}
              label="Profile completeness"
            />
          </Card>

          <div className="accordion" id="profile-accordion">
            <AccordionItem id="personal" title="Personal Info" defaultOpen>
              <BasicDetailsForm profile={profile} />
              <div className="mt-3 d-flex flex-wrap gap-3">
                <FieldVisibilityToggle
                  fieldKey="dob"
                  label="Date of birth"
                  visible={profile.fieldVisibility.dob ?? true}
                />
                <FieldVisibilityToggle
                  fieldKey="contact_number"
                  label="Contact number"
                  visible={profile.fieldVisibility.contact_number ?? true}
                />
              </div>
            </AccordionItem>

            <AccordionItem id="preferences" title="Career Preferences & Salary">
              <PreferencesForm profile={profile} />
              <div className="mt-3 d-flex flex-wrap gap-3">
                <FieldVisibilityToggle
                  fieldKey="current_ctc"
                  label="Current CTC"
                  visible={profile.fieldVisibility.current_ctc ?? true}
                />
                <FieldVisibilityToggle
                  fieldKey="expected_ctc"
                  label="Expected CTC"
                  visible={profile.fieldVisibility.expected_ctc ?? true}
                />
              </div>
            </AccordionItem>

            <AccordionItem id="education" title={`Education (${profile.educations.length})`}>
              <EducationSection items={profile.educations} />
            </AccordionItem>

            <AccordionItem
              id="experience"
              title={`Work Experience (${profile.experiences.length})`}
            >
              <ExperienceSection items={profile.experiences} />
              <div className="mt-3">
                <FieldVisibilityToggle
                  fieldKey="current_company"
                  label="Current company"
                  visible={profile.fieldVisibility.current_company ?? true}
                />
              </div>
            </AccordionItem>

            <AccordionItem id="skills" title={`Skills (${profile.skills.length})`}>
              <SkillSection items={profile.skills} />
            </AccordionItem>

            <AccordionItem id="projects" title={`Projects (${profile.projects.length})`}>
              <ProjectSection items={profile.projects} />
            </AccordionItem>

            <AccordionItem
              id="certifications"
              title={`Certifications (${profile.certifications.length})`}
            >
              <CertificationSection items={profile.certifications} />
            </AccordionItem>

            <AccordionItem id="languages" title={`Languages (${profile.languages.length})`}>
              <LanguageSection items={profile.languages} />
            </AccordionItem>

            <AccordionItem id="links" title={`Social Links (${profile.socialLinks.length})`}>
              <SocialLinkSection items={profile.socialLinks} />
            </AccordionItem>

            <AccordionItem id="documents" title="Resume, Photo & Documents">
              <DocumentsSection profile={profile} />
            </AccordionItem>
          </div>
        </>
      )}
    </>
  )
}
